import React, { useState, useEffect, useCallback } from 'react';
import Panel from './Panel';
import { Globe, MessageSquare, MessageCircle, Facebook, Copy, Check as CheckIcon } from 'lucide-react';
import { listChannelBindings } from '../utils/channelApi';
import { READY_TO_DEPLOY_ACTIONS, DEPLOYED_ACTIONS } from './panelActions';
import useContextMenu from '../hooks/useContextMenu';
import ContextMenu from './ContextMenu';
import useLayoutDrag from '../hooks/useLayoutDrag';
import TextInputModal from './TextInputModal';
import ShareSurveyModal from './ShareSurveyModal';
import DeployedRowStatusBadge from './DeployedRowStatusBadge';
import { openExplorer } from '../utils/explorerLaunch';
import { getServiceUrl } from '../sysconfig';
import { isReadySurveyDragPayload } from '../utils/surveyDrag';

// Public survey link base — survey.samsurveyor.com fronts channel-gate.
// Can be overridden by a 'presentor' entry in sysconfig services.
const PUBLIC_PRESENTOR_BASE = 'https://survey.samsurveyor.com';

// Channel type → Lucide icon mapping
const CHANNEL_ICONS = {
    browser: { Icon: Globe, label: 'Browser' },
    sms: { Icon: MessageSquare, label: 'SMS' },
    whatsapp: { Icon: MessageCircle, label: 'WhatsApp' },
    messenger: { Icon: Facebook, label: 'Messenger' },
};

function firstFinite(...values) {
    for (const value of values) {
        const number = Number(value);
        if (Number.isFinite(number)) return number;
    }
    return 0;
}

/**
 * Renders a row of channel icons for a deployment.
 * Enabled channels are shown in primary blue; disabled in neutral gray.
 */
function ChannelIcons({ bindings }) {
    if (!bindings || bindings.length === 0) return null;
    return (
        <span style={{ display: 'inline-flex', gap: 4, marginLeft: 6, verticalAlign: 'middle' }}>
            {bindings.map(b => {
                const ch = CHANNEL_ICONS[b.channel_type];
                if (!ch) return null;
                const { Icon, label } = ch;
                return (
                    <span key={b.channel_type} title={`${label}${b.enabled ? '' : ' (disabled)'}`}>
                        <Icon
                            size={14}
                            strokeWidth={1.5}
                            color={b.enabled ? '#5B9FFF' : '#BDC1C6'}
                        />
                    </span>
                );
            })}
        </span>
    );
}

export function ReadyToDeployPanel({
    readyToDeploy,
    deployments,
    onUndeploy,
    selectedSurveyId,
    setSelectedSurveyId,
    setSelectedDeploymentId,
    openDesigner,
    onNewSurvey,
    onDeploy,
    onCopy,
    onDeleteSurvey,
    onBulkDeleteSurveys,
    onShareSurvey,
    onArchiveSurvey,
    onBulkArchiveSurveys,
    archiveFileOpen,
    archiveHasContent,
}) {
    const { openMenu, contextMenuProps } = useContextMenu();
    const { dragHandlers, dropHandlers, activeDropTarget } = useLayoutDrag();
    const [shareModal, setShareModal] = useState(null); // { surveyId, surveyName }
    const readyDropActive = activeDropTarget === 'ready-to-deploy';

    // Multi-select
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmDeleteIds, setConfirmDeleteIds] = useState(null);

    // Filtered list
    const filtered = searchTerm
        ? readyToDeploy.filter(s => (s.name || s.survey_id || '').toLowerCase().includes(searchTerm.toLowerCase()))
        : readyToDeploy;

    const filteredIds = new Set(filtered.map(s => s.survey_id));
    const visibleSelectedCount = [...selectedIds].filter(id => filteredIds.has(id)).length;
    const allVisibleSelected = filtered.length > 0 && visibleSelectedCount === filtered.length;

    const toggleSelectAll = () => {
        if (allVisibleSelected) {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(s => next.delete(s.survey_id));
                return next;
            });
        } else {
            setSelectedIds(prev => {
                const next = new Set(prev);
                filtered.forEach(s => next.add(s.survey_id));
                return next;
            });
        }
    };

    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Resolve effective targets: if multi-select has items use those, else fall back to single selectedSurveyId
    const effectiveIds = selectedIds.size > 0 ? [...selectedIds] : (selectedSurveyId ? [selectedSurveyId] : []);
    const multiCount = selectedIds.size;
    const labelSuffix = multiCount > 1 ? ` (${multiCount})` : '';

    // Bulk action handlers — sequential to avoid races on shared resources
    const handleBulkDeploy = async () => {
        for (const id of effectiveIds) { await onDeploy?.(id); }
    };
    const handleBulkDuplicate = async () => {
        for (const id of effectiveIds) { await onCopy?.(id); }
    };
    const handleBulkArchive = async () => {
        if (onBulkArchiveSurveys && effectiveIds.length > 1) {
            await onBulkArchiveSurveys(effectiveIds);
        } else {
            for (const id of effectiveIds) { await onArchiveSurvey?.(id); }
        }
        setSelectedIds(new Set());
    };
    const handleBulkDelete = () => {
        if (effectiveIds.length === 0) return;
        setConfirmDeleteIds(effectiveIds);
    };
    const confirmDelete = async () => {
        if (!confirmDeleteIds) return;
        // This modal is already the confirmation step, so delete directly via
        // the bulk handler. The per-survey onDeleteSurvey opens its OWN confirm
        // modal and never resolves inside a loop, which is why bulk delete
        // previously did nothing. Fall back to it only if the bulk prop is
        // absent (single-survey callers still use onDeleteSurvey elsewhere).
        if (onBulkDeleteSurveys) {
            await onBulkDeleteSurveys(confirmDeleteIds);
        } else {
            for (const id of confirmDeleteIds) { await onDeleteSurvey?.(id); }
        }
        setSelectedIds(new Set());
        setConfirmDeleteIds(null);
    };

    const actions = READY_TO_DEPLOY_ACTIONS.map(a => {
        switch (a.label) {
            case '+ New':
                return { ...a, onClick: () => onNewSurvey?.() };
            case 'Edit':
                return { ...a, onClick: () => selectedSurveyId && openDesigner?.(selectedSurveyId), label: 'Edit' };
            case 'Duplicate':
                return { ...a, onClick: handleBulkDuplicate, label: `Duplicate${labelSuffix}` };
            case 'Deploy':
                return { ...a, onClick: handleBulkDeploy, label: `Deploy${labelSuffix}` };
            case 'Delete':
                return { ...a, onClick: handleBulkDelete, label: `Delete${labelSuffix}` };
            case 'Share':
                return {
                    ...a,
                    onClick: () => {
                        const id = effectiveIds.length === 1 ? effectiveIds[0] : selectedSurveyId;
                        if (!id) return;
                        const s = readyToDeploy.find(x => x.survey_id === id);
                        setShareModal({ surveyId: id, surveyName: s?.name || id });
                    },
                };
            case 'Archive':
                return { ...a, onClick: handleBulkArchive, label: `Archive${labelSuffix}` };
            default:
                return a;
        }
    });

    return (
        <>
            <Panel title="Ready" icon="ready" actions={actions} selection={selectedSurveyId || (selectedIds.size > 0 ? 'multi' : null)} actionState={{ selectedSurvey: { status: 'ready' }, archiveFileOpen, archiveHasContent, multiCount }} style={{ height: '100%', boxSizing: 'border-box' }}>
                {/* Search bar */}
                <div style={{ padding: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                        type="text"
                        placeholder="Search surveys…"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        style={{
                            flex: 1, padding: '5px 8px', fontSize: 12,
                            border: '1px solid #E8EAED', borderRadius: 4,
                            outline: 'none', background: '#FAFBFC',
                        }}
                    />
                    {searchTerm && (
                        <button onClick={() => setSearchTerm('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#5F6368', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
                    )}
                </div>
                {/* Header checkbox row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 6px', borderBottom: '1px solid #E8EAED', fontSize: 11, color: '#5F6368' }}>
                    <input type="checkbox" checked={allVisibleSelected && filtered.length > 0} onChange={toggleSelectAll} style={{ margin: 0 }} />
                    <span>{filtered.length} survey{filtered.length !== 1 ? 's' : ''}{selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''}</span>
                </div>
                <div
                    {...dropHandlers(
                        'ready-to-deploy',
                        (p) => p.type === 'survey' && p.status === 'deployed',
                        // WP3: undeploy is server-decided. Call it directly; the
                        // completion-check confirmation (if any) is raised by App
                        // as a styled ConfirmModal, never here.
                        (payload) => onUndeploy?.(payload.deploymentId)
                    )}
                    style={{
                        minHeight: 48,
                        border: readyDropActive ? '2px dashed #1967D2' : '2px dashed transparent',
                        background: readyDropActive ? '#E8F0FE' : undefined,
                        borderRadius: 4,
                        transition: 'background 120ms ease, border-color 120ms ease',
                    }}
                >
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {filtered.map(s => {
                            const openDep = (deployments || []).find(d => d.survey_id === s.survey_id && d.is_open);
                            const selected = selectedSurveyId === s.survey_id;
                            const checked = selectedIds.has(s.survey_id);
                            const ver = s.version || s.latest_version_number || 1;
                            const ts = s.updated_at || s.last_updated || '';
                            const shortTs = (() => {
                                if (!ts) return '';
                                const d = new Date(ts);
                                if (Number.isNaN(d.getTime())) return '';
                                const month = d.toLocaleString(undefined, { month: 'short' });
                                const day = d.getDate();
                                const h24 = d.getHours();
                                const h12 = (h24 % 12) || 12;
                                const min = String(d.getMinutes()).padStart(2, '0');
                                const ampm = h24 >= 12 ? 'PM' : 'AM';
                                return `${month}${day} ${h12}:${min}${ampm}`;
                            })();
                            return (
                                <li key={s.survey_id} {...dragHandlers({
                                    type: 'survey',
                                    surveyId: s.id || s.survey_id,
                                    surveyName: s.name || s.title || s.survey_id,
                                    status: 'ready',
                                    linkedRecipientCount: 0,
                                })} onClick={() => { if (selected) { setSelectedSurveyId(null); setSelectedDeploymentId(null); } else { setSelectedSurveyId(s.survey_id); setSelectedDeploymentId(null); } }} onContextMenu={(e) => {
                                    setSelectedSurveyId(s.survey_id);
                                    setSelectedDeploymentId(null);
                                    const items = actions
                                        .filter(a => !a.hideWhen || !a.hideWhen({ selectedSurvey: { status: 'ready' } }))
                                        .map(a => {
                                            const rowOnClick = (() => {
                                                switch (a.label.replace(/ \(\d+\)$/, '')) {
                                                    case '+ New': return () => onNewSurvey?.();
                                                    case 'Edit': return () => openDesigner?.(s.survey_id);
                                                    case 'Duplicate': return () => onCopy?.(s.survey_id);
                                                    case 'Deploy': return () => onDeploy?.(s.survey_id);
                                                    case 'Delete': return () => onDeleteSurvey?.(s.survey_id);
                                                    case 'Archive': return () => onArchiveSurvey?.(s.survey_id);
                                                    default: return a.onClick;
                                                }
                                            })();
                                            const dimmed = (a.disabledWhen && a.disabledWhen({ archiveFileOpen, archiveHasContent }))
                                                || (a.requiresSelection && !s.survey_id);
                                            return { label: a.label, onClick: dimmed ? undefined : rowOnClick, dimmed };
                                        });
                                    openMenu(e, items);
                                }} style={{ padding: '6px 4px', borderBottom: '1px solid #eee', cursor: 'pointer', background: selected ? '#eef2ff' : checked ? '#f0f7ff' : undefined, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                    <input type="checkbox" checked={checked} onChange={(e) => { e.stopPropagation(); toggleSelect(s.survey_id); }} onClick={e => e.stopPropagation()} style={{ marginTop: 3, flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>{s.name || s.survey_id}</div>
                                        <div style={{ fontSize: 12, color: '#666' }}>
                                            {shortTs && (<span>{shortTs}</span>)}{shortTs && ' · '}<span>v{ver}</span>
                                            {openDep && (
                                                <>
                                                    {' · '}<a href={openDep.url} target="_blank" rel="noreferrer">Presentor</a>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </Panel>
            {contextMenuProps && <ContextMenu {...contextMenuProps} />}
            {/* Share survey with another account */}
            <ShareSurveyModal
                open={!!shareModal}
                surveyName={shareModal?.surveyName || ''}
                onSubmit={({ toEmail, message }) => {
                    const target = shareModal;
                    setShareModal(null);
                    if (target) onShareSurvey?.(target.surveyId, toEmail, message);
                }}
                onCancel={() => setShareModal(null)}
            />
            {/* Bulk delete confirmation */}
            {confirmDeleteIds && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ background: '#fff', borderRadius: 12, padding: 24, maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
                        <h3 style={{ margin: '0 0 12px' }}>Delete {confirmDeleteIds.length} survey{confirmDeleteIds.length !== 1 ? 's' : ''}?</h3>
                        <p style={{ margin: '0 0 20px', color: '#5F6368', fontSize: 14 }}>
                            This will permanently delete the selected surveys. This action cannot be undone.
                        </p>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setConfirmDeleteIds(null)} style={{ padding: '8px 16px', border: '1px solid #ddd', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Cancel</button>
                            <button onClick={confirmDelete} style={{ padding: '8px 16px', border: 'none', borderRadius: 6, background: '#DC3545', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>Delete</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export function DeployedPanel({
    deployments,
    surveys,
    surveyStatusById,
    onDeploy,
    onUndeploy,
    onSetCustomSlug,
    selectedDeploymentId,
    setSelectedDeploymentId,
    setSelectedSurveyId,
    onLoadRecipients,
    openDesigner,
}) {
    const { openMenu, contextMenuProps } = useContextMenu();
    const { dragHandlers, dropHandlers, activeDropTarget } = useLayoutDrag();
    const [customSlugModal, setCustomSlugModal] = useState(null); // { deploymentId, currentSlug }
    const deployedDropActive = activeDropTarget === 'deployed';
    const [copiedSlug, setCopiedSlug] = useState(null);

    // Multi-select
    const [selectedDepIds, setSelectedDepIds] = useState(new Set());

    // Channel bindings keyed by deployment_id
    const [channelMap, setChannelMap] = useState({});

    // Public presentor base URL for displayed/copied survey links.
    // Prefer a 'presentor' entry in sysconfig; fall back to the public domain
    // (which fronts channel-gate). The internal channel-gate run.app URL is
    // never shown to users.
    const [presentorBase, setPresentorBase] = useState(PUBLIC_PRESENTOR_BASE);
    useEffect(() => {
        getServiceUrl('presentor').then(url => {
            if (url) setPresentorBase(url.replace(/\/+$/, ''));
        }).catch(() => { });
    }, []);

    // WP3: the Deployed panel renders FROM the server lifecycle state. A row
    // shows when its deployment is open AND the survey is NOT staged — i.e.
    // 'deployed' (never sent) or 'sent' (live). Staged surveys live in the
    // Staging Area; closed deployments are Dispatched history. Keeping live
    // 'sent' surveys here preserves the Undeploy affordance (Undeploy action and
    // drag-to-Ready) for the completion-checked sent → ready close. When no
    // status map is provided the panel falls back to the raw is_open filter so
    // it degrades gracefully.
    const lifecycleOf = (surveyId) => (surveyStatusById ? surveyStatusById.get(surveyId) : undefined);
    const openDeployments = (deployments || []).filter(d => {
        if (!d.is_open) return false;
        const st = lifecycleOf(d.survey_id);
        return st === undefined ? true : (st === 'deployed' || st === 'sent');
    });

    const allSelected = openDeployments.length > 0 && openDeployments.every(d => selectedDepIds.has(d.deployment_id));
    const toggleSelectAll = () => {
        if (allSelected) {
            setSelectedDepIds(new Set());
        } else {
            setSelectedDepIds(new Set(openDeployments.map(d => d.deployment_id)));
        }
    };
    const toggleSelect = (id) => {
        setSelectedDepIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const multiCount = selectedDepIds.size;
    const labelSuffix = multiCount > 1 ? ` (${multiCount})` : '';

    // Helper: build an undeploy handler for a given deployment. WP3 — undeploy is
    // server-decided; call it directly. Any completion-check confirmation is
    // raised by App as a styled ConfirmModal.
    const buildUndeployForRow = (d) => () => onUndeploy?.(d.deployment_id);

    // Bulk undeploy handler. No confirmation of its own: undeploy is decided
    // server-side, and App raises the styled ConfirmModal only when the server
    // reports a completion shortfall. A blanket prompt here asked twice for the
    // live case and asked for nothing on the common case, where undeploy is a
    // silent close of a deployment that never sent anything and is reversible
    // by deploying again.
    const handleBulkUndeploy = async () => {
        const ids = selectedDepIds.size > 0 ? [...selectedDepIds] : (selectedDeploymentId ? [selectedDeploymentId] : []);
        if (ids.length === 0) return;
        for (const id of ids) { await onUndeploy?.(id); }
        setSelectedDepIds(new Set());
    };

    const selectedDeployment = openDeployments.find(d => d.deployment_id === selectedDeploymentId);
    const actions = DEPLOYED_ACTIONS.map(a => {
        switch (a.label) {
            // 'Edit' removed from DEPLOYED_ACTIONS 2026-07-31 — deployed
            // versions are pinned/immutable; editing lives on the Ready panel.
            case 'Custom':
                return {
                    ...a,
                    onClick: () => selectedDeployment && setCustomSlugModal({
                        deploymentId: selectedDeployment.deployment_id,
                        currentSlug: selectedDeployment.deploy_slug || selectedDeployment.deployment_slug || selectedDeployment.slug || '',
                    }),
                };
            case 'Undeploy':
                return { ...a, onClick: handleBulkUndeploy, label: `Undeploy${labelSuffix}` };
            default:
                return a;
        }
    });

    // Fetch channel bindings for all open deployments
    const fetchChannels = useCallback(async () => {
        if (openDeployments.length === 0) return;
        const entries = {};
        await Promise.allSettled(
            openDeployments.map(async (d) => {
                try {
                    const bindings = await listChannelBindings(d.deployment_id);
                    entries[d.deployment_id] = bindings;
                } catch (err) {
                    console.warn(`[DeployedPanel] Failed to fetch channels for ${d.deployment_id}:`, err.message);
                    entries[d.deployment_id] = [];
                }
            })
        );
        setChannelMap(entries);
    }, [openDeployments.map(d => d.deployment_id).join(',')]);

    useEffect(() => {
        fetchChannels();
    }, [fetchChannels]);

    return (
        <>
            <Panel title="Deployed" icon="deployed" actions={actions} selection={selectedDeploymentId || (selectedDepIds.size > 0 ? 'multi' : null)} actionState={{ selectedSurvey: { status: 'deployed' }, multiCount }} style={{ height: '100%', boxSizing: 'border-box' }}>
                {/* Header checkbox row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 4px 6px', borderBottom: '1px solid #E8EAED', fontSize: 11, color: '#5F6368' }}>
                    <input type="checkbox" checked={allSelected && openDeployments.length > 0} onChange={toggleSelectAll} style={{ margin: 0 }} />
                    <span>{openDeployments.length} deployment{openDeployments.length !== 1 ? 's' : ''}{selectedDepIds.size > 0 ? ` · ${selectedDepIds.size} selected` : ''}</span>
                </div>
                <div
                    {...dropHandlers(
                        'deployed',
                        isReadySurveyDragPayload,
                        (payload) => onDeploy?.(payload.surveyId)
                    )}
                    style={{
                        minHeight: 48,
                        border: deployedDropActive ? '2px dashed #1967D2' : '2px dashed transparent',
                        background: deployedDropActive ? '#E8F0FE' : undefined,
                        borderRadius: 4,
                        transition: 'background 120ms ease, border-color 120ms ease',
                    }}
                >
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                        {openDeployments.map(d => {
                            const selected = selectedDeploymentId === d.deployment_id;
                            const checked = selectedDepIds.has(d.deployment_id);
                            const bindings = channelMap[d.deployment_id] || [];
                            const surveyName = d.survey_name || ((surveys || []).find(s => s.survey_id === d.survey_id)?.name) || d.survey_id;
                            const linkedRecipientCount = d.linkedRecipientCount || d.linked_recipient_count || d.recipient_count || 0;
                            const responseCount = firstFinite(
                                d.responseCount,
                                d.response_count,
                                d.completedRecipientCount,
                                d.completed_recipient_count,
                                d.completed_count,
                                d.responses_completed,
                                d.completion_count
                            );
                            const sentCount = firstFinite(
                                d.sentCount,
                                d.sent_count,
                                d.sentRecipientCount,
                                d.sent_recipient_count,
                                d.outreach_sent_count,
                                d.invitation_sent_count,
                                d.sent,
                                linkedRecipientCount
                            );
                            const lastActivityAt = d.lastActivityAt || d.last_activity_at || d.last_response_at || d.updated_at;
                            return (
                                <li key={d.deployment_id} {...dragHandlers({
                                    type: 'survey',
                                    surveyId: d.survey_id,
                                    surveyName,
                                    deploymentId: d.deployment_id,
                                    deploymentSlug: d.deploy_slug || d.deployment_slug || d.slug || d.deployment_id,
                                    status: 'deployed',
                                    // True server lifecycle state ('deployed' | 'sent'). The Deployed
                                    // panel renders both, but `status` above stays 'deployed' so the
                                    // existing drop predicates (Staging Area, Ready-to-Deploy) are
                                    // unaffected. `lifecycleStatus` lets the Staging Area drop path
                                    // detect a 'sent' survey and re-deploy it for send.
                                    lifecycleStatus: lifecycleOf(d.survey_id) || 'deployed',
                                    linkedRecipientCount,
                                    designedChannels: d.designed_channels || d.target_channels || ['browser'],
                                    surveyVersionId: d.survey_version_id || null,
                                })} onClick={() => { setSelectedDeploymentId(d.deployment_id); setSelectedSurveyId(d.survey_id); onLoadRecipients(d.deployment_id); }} onDoubleClick={() => {
                                    openExplorer({
                                        surveyId: d.survey_id || d.id,
                                        deploymentId: d.deployment_id,
                                        tab: 'explore',
                                    }).catch(err => console.error('Open Explorer failed:', err));
                                }} onContextMenu={(e) => {
                                    // Select this row first so subsequent top-bar actions stay
                                    // consistent, then build per-row handlers that close over
                                    // d, surveyName, and linkedRecipientCount directly (so we
                                    // don't depend on selection state having flushed).
                                    setSelectedDeploymentId(d.deployment_id);
                                    setSelectedSurveyId(d.survey_id);
                                    const items = DEPLOYED_ACTIONS
                                        .filter(a => !a.hideWhen || !a.hideWhen({ selectedSurvey: { status: 'deployed' } }))
                                        .map(a => {
                                            const rowOnClick = (() => {
                                                switch (a.label) {
                                                    // 'Edit' removed 2026-07-31 — deployed versions are pinned.
                                                    case 'Custom': return () => setCustomSlugModal({
                                                        deploymentId: d.deployment_id,
                                                        currentSlug: d.deploy_slug || d.deployment_slug || d.slug || '',
                                                    });
                                                    case 'Undeploy': return buildUndeployForRow(d);
                                                    default: return a.onClick;
                                                }
                                            })();
                                            return { label: a.label, onClick: rowOnClick, dimmed: false };
                                        });
                                    openMenu(e, items);
                                }} style={{ padding: '6px 4px', borderBottom: '1px solid #E8EAED', cursor: 'pointer', background: selected ? '#EBF4FF' : checked ? '#f0f7ff' : undefined, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                                    <input type="checkbox" checked={checked} onChange={(e) => { e.stopPropagation(); toggleSelect(d.deployment_id); }} onClick={e => e.stopPropagation()} style={{ marginTop: 3, flexShrink: 0 }} />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <strong style={{ color: '#202124' }}>{surveyName}</strong>
                                            <ChannelIcons bindings={bindings} />
                                            <span style={{ marginLeft: 'auto' }}>
                                                <DeployedRowStatusBadge
                                                    responseCount={responseCount}
                                                    sentCount={sentCount}
                                                    lastActivityAt={lastActivityAt}
                                                />
                                            </span>
                                        </div>
                                        <div style={{ fontSize: 12, color: '#5F6368' }}>
                                            {d.is_open ? 'Active' : 'Closed'} · Created: {d.created_at ? new Date(d.created_at).toLocaleDateString() : 'N/A'}
                                        </div>
                                        {(() => {
                                            const slug = d.deploy_slug || d.deployment_slug || d.slug;
                                            if (!slug || !presentorBase) return null;
                                            const surveyUrl = `${presentorBase}/d/${slug}`;
                                            const justCopied = copiedSlug === slug;
                                            return (
                                                <div
                                                    style={{ fontSize: 11, color: '#1967D2', marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <a href={surveyUrl} target="_blank" rel="noreferrer" style={{ color: '#1967D2', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }} title={surveyUrl}>
                                                        {surveyUrl.replace(/^https?:\/\//, '')}
                                                    </a>
                                                    <button
                                                        type="button"
                                                        title="Copy survey link"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigator.clipboard.writeText(surveyUrl).then(() => {
                                                                setCopiedSlug(slug);
                                                                setTimeout(() => setCopiedSlug(null), 2000);
                                                            });
                                                        }}
                                                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: justCopied ? '#2e7d32' : '#1967D2' }}
                                                    >
                                                        {justCopied ? <CheckIcon size={12} /> : <Copy size={12} />}
                                                    </button>
                                                    {justCopied && <span style={{ fontSize: 10, color: '#2e7d32' }}>Copied!</span>}
                                                </div>
                                            );
                                        })()}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </Panel>
            {contextMenuProps && <ContextMenu {...contextMenuProps} />}
            {/* Custom URL slug entry */}
            <TextInputModal
                open={!!customSlugModal}
                title="Custom URL Slug"
                message={<>The survey link becomes <code>{presentorBase}/d/&lt;slug&gt;</code>. Letters, digits, hyphens, and underscores only.</>}
                label="Slug"
                initialValue={customSlugModal?.currentSlug || ''}
                placeholder="e.g. portfolio"
                confirmLabel="Apply"
                onSubmit={(value) => {
                    const dep = customSlugModal;
                    setCustomSlugModal(null);
                    if (dep && value && value !== dep.currentSlug) {
                        onSetCustomSlug?.(dep.deploymentId, value);
                    }
                }}
                onCancel={() => setCustomSlugModal(null)}
            />
        </>
    );
}
